import { createSlice } from '@reduxjs/toolkit'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey';
import { AppIsInTestEnvironment } from 'functions/environmentUtils'
import moment from 'moment'

const initialState = {
    profileType: null,
    groupId: null,
    groupOwnerUserId: null,
    groupName: null,
    groupDescription: null,
    groupAthletes: null,
    groupIndividualCompletionData: [],
    userId: null,
    firstName: null,
    lastName: null,
    email: null,
    type: null,
    coachInviteEmail: null,
    coachInviteSignUpCode: null,
    latestCoachInviteTimestamp: null,
    organizationTrainerId: null,
    terraUserId: null,
    trainingPeaksMappingAssignerCoachId: null,
    trainingPeaksUserId: null,
    defaultWeekDaySchedule: null,
    overrideWeekDaySchedules: [],
    kinesisEvents: [],
    pastData: [],
    activeData: null,
    latestProgramSnapshot: null,
    complianceMobilityWeeklyRequirement: null,
    complianceStrengthWeeklyRequirement: null,
    complianceWeekSpan: null,
    complianceData: null,
    trainingChartPresentDate: moment.utc().local().format('YYYY-MM-DD'),
    trainingChartPresentWeekOfTheYearKey: new WeekOfTheYearKey(moment.utc().local()).toString(),
    trainingChartSelectedWeekOfTheYearKey: new WeekOfTheYearKey(moment.utc().local()).toString(),
    trainingChartUILocation: '',
    trainingChartSegmentDomain: 33,
    trainingChartSegment: [],
    trainingChartSelectedWeekData: null,
    trainingChartSelectedActiveARaceProgramTableColumn: null,
    trainingPeaksWorkoutCalendarEventItems: [],
    programTableOverrides: [],
    workoutTemplateOverrideWeightRecommendationStorage: null
}

const athleteProfileSlice = createSlice({
    name: 'athleteProfile',
    initialState,
    reducers: {
        initializeGroupProfileData: (state, action) => {

            const { 
                profileType,
                groupId, 
                groupOwnerUserId, 
                groupName, 
                groupDescription, 
                groupAthletes,
                groupIndividualCompletionData,
                complianceMobilityWeeklyRequirement,
                complianceStrengthWeeklyRequirement,
                complianceWeekSpan,
                defaultWeekDaySchedule, 
                overrideWeekDaySchedules, 
                kinesisEvents, 
                pastData, 
                activeData, 
                latestProgramSnapshot,
                complianceData
            } = action.payload

            state.profileType = profileType
            state.groupId = groupId
            state.groupOwnerUserId = groupOwnerUserId
            state.groupName = groupName
            state.groupDescription = groupDescription
            state.groupAthletes = groupAthletes
            state.groupIndividualCompletionData = groupIndividualCompletionData
            state.userId = null
            state.firstName = null
            state.lastName = null
            state.email = null
            state.type = null
            state.coachInviteEmail = null
            state.coachInviteSignUpCode = null
            state.latestCoachInviteTimestamp = null
            state.organizationTrainerId = null
            state.terraUserId = null
            state.trainingPeaksMappingAssignerCoachId = null
            state.trainingPeaksUserId = null
            state.trainingChartUILocation = 'athlete-profile'
            state.defaultWeekDaySchedule = defaultWeekDaySchedule
            state.overrideWeekDaySchedules = overrideWeekDaySchedules
            state.kinesisEvents = kinesisEvents
            state.pastData = pastData
            state.activeData = activeData
            state.latestProgramSnapshot = latestProgramSnapshot
            state.complianceMobilityWeeklyRequirement = complianceMobilityWeeklyRequirement
            state.complianceStrengthWeeklyRequirement = complianceStrengthWeeklyRequirement
            state.complianceWeekSpan = complianceWeekSpan
            state.complianceData = complianceData

            state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            CreateTrainingChartSegment(state)

        },
        initializeAthleteProfileData: (state, action) => {

            const { 
                profileType,
                groupId,
                groupOwnerUserId,
                groupName,
                groupDescription,
                userId,
                firstName,
                lastName,
                email,
                type,
                coachInviteEmail,
                coachInviteSignUpCode,
                latestCoachInviteTimestamp,
                organizationTrainerId,
                terraUserId,
                trainingPeaksMappingAssignerCoachId,
                trainingPeaksUserId,
                defaultWeekDaySchedule,
                overrideWeekDaySchedules,
                kinesisEvents,
                pastData,
                activeData,
                latestProgramSnapshot
            } = action.payload

            state.profileType = profileType
            state.groupId = groupId
            state.groupOwnerUserId = groupOwnerUserId
            state.groupName = groupName
            state.groupDescription = groupDescription
            state.userId = userId
            state.firstName = firstName
            state.lastName = lastName
            state.email = email
            state.type = type
            state.coachInviteEmail = coachInviteEmail ? coachInviteEmail : ''
            state.coachInviteSignUpCode = coachInviteSignUpCode ? coachInviteSignUpCode : ''
            state.latestCoachInviteTimestamp = latestCoachInviteTimestamp
            state.organizationTrainerId = organizationTrainerId
            state.terraUserId = terraUserId
            state.trainingPeaksMappingAssignerCoachId = trainingPeaksMappingAssignerCoachId
            state.trainingPeaksUserId = trainingPeaksUserId
            state.trainingChartUILocation = 'athlete-profile'
            state.defaultWeekDaySchedule = defaultWeekDaySchedule
            state.overrideWeekDaySchedules = overrideWeekDaySchedules
            state.kinesisEvents = kinesisEvents
            state.pastData = pastData
            state.activeData = activeData
            state.latestProgramSnapshot = latestProgramSnapshot

            if (state.type === 'trainee') {
                state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
                state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
                CreateTrainingChartSegment(state)
            }

        },
        setTrainingChartSelectedWeekOfTheYearKey: (state, action) => {

            state.trainingChartSelectedWeekOfTheYearKey = action.payload

            state.trainingChartSelectedWeekData = GetTrainingChartWeekData(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))
            state.trainingChartSelectedActiveARaceProgramTableColumn = GetActiveARaceProgramTableColumn(state, new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey))

            CreateTrainingChartSegment(state)

        },
        setActiveWeekWeekDaySchedule: (state, action) => { state.activeData.weekDaySchedule = action.payload },
        setActiveWeekCompletionData: (state, action) => { state.activeData.completionData[action.payload.workoutTemplateKey] = action.payload.defaultCompletionData },
        addOverrideWeekDaySchedule: (state, action) => {
            // Remove existing override for this week if it exists
            state.overrideWeekDaySchedules = state.overrideWeekDaySchedules.filter(
                override => override.weekOfTheYearKey !== action.payload.weekOfTheYearKey
            );

            // Add new override
            state.overrideWeekDaySchedules.push(action.payload);
        },
        removeOverrideWeekDaySchedule: (state, action) => {
            // Remove override for this week
            state.overrideWeekDaySchedules = state.overrideWeekDaySchedules.filter(
                override => override.weekOfTheYearKey !== action.payload.weekOfTheYearKey
            );
        },
        addTrainingPeaksWorkoutCalendarEventItems: (state, action) => {

            for (let new_item of action.payload) {

                const newWorkoutId = new_item.extendedProps?.trainingPeaksWorkoutId

                // Skip if no workout ID
                if (!newWorkoutId) continue

                // Check if this workout already exists
                const alreadyExists = state.trainingPeaksWorkoutCalendarEventItems.some(
                    stored_item => stored_item.extendedProps?.trainingPeaksWorkoutId === newWorkoutId
                )

                // Only add if it doesn't already exist
                if (!alreadyExists) {
                    state.trainingPeaksWorkoutCalendarEventItems.push(new_item)
                }

            }

        },
        setCoachInviteEmail: (state, action) => {
            state.coachInviteEmail = action.payload
        },
        setLatestCoachInviteTimestamp: (state, action) => {
            state.latestCoachInviteTimestamp = action.payload
        },
        addNewProgramTableOverride: (state, action) => { state.programTableOverrides.push(action.payload) },
        removeProgramTableOverride: (state, action) => {
          const filtered = state.programTableOverrides.filter((_, index) => index !== action.payload);
          state.programTableOverrides = filtered.length === 0 ? [] : filtered;
        },
        clearProgramTableOverrides: (state) => { state.programTableOverrides = [] },
        setWorkoutTemplateOverrideWeightRecommendationStorage: (state, action) => { state.workoutTemplateOverrideWeightRecommendationStorage = action.payload },
        updateWorkoutTemplateOverrideWeightRecommendationStorage: (state, action) => { state.workoutTemplateOverrideWeightRecommendationStorage[action.payload.phaseIndex][action.payload.slotIndex] = action.payload.value },
        clearWorkoutTemplateOverrideWeightRecommendationStorage: (state) => { state.workoutTemplateOverrideWeightRecommendationStorage = null },
        setTrainingChartSegmentDomain: (state, action) => {
            state.trainingChartSegmentDomain = action.payload;
            CreateTrainingChartSegment(state);
        },
        resetAthleteProfileState: (state) => {
            
            // Do not reset the training chart present date and week of the year key
            // Used for program simulation
            const cachedTrainingChartPresentDate = state.trainingChartPresentDate
            const cachedTrainingChartPresentWeekOfTheYearKey = state.trainingChartPresentWeekOfTheYearKey

            var resetState = JSON.parse(JSON.stringify(initialState))
            resetState.trainingChartPresentDate = cachedTrainingChartPresentDate
            resetState.trainingChartPresentWeekOfTheYearKey = cachedTrainingChartPresentWeekOfTheYearKey
            resetState.trainingChartSelectedWeekOfTheYearKey = cachedTrainingChartPresentWeekOfTheYearKey

            return resetState;
        },
        TEST_setPresentDate: (state, action) => { 
            if (AppIsInTestEnvironment()) {
                state.trainingChartPresentDate = action.payload
            } 
        },
        TEST_setPresentWeekOfTheYearKey: (state, action) => { 
            if (AppIsInTestEnvironment()) {
                state.trainingChartPresentWeekOfTheYearKey = action.payload
                state.trainingChartSelectedWeekOfTheYearKey = action.payload
            } 
        }
    }
});

const CreateTrainingChartSegment = (state) => {

    var newTrainingChartSegment = []

    // Get week of the year keys for the entire segment
    var segmentWeekOfTheYearKeys = []
    const segment_domain_half = Math.floor(state.trainingChartSegmentDomain / 2)
    for (let i = -segment_domain_half; i <= segment_domain_half; i++) segmentWeekOfTheYearKeys.push(new WeekOfTheYearKey(state.trainingChartSelectedWeekOfTheYearKey).add(i))

    // Loop over every week of the year key and find the matching week in the training chart data
    segmentWeekOfTheYearKeys.forEach((weekOfTheYearKey) => {

        var item = CreateTrainingChartSegmentObject(state, weekOfTheYearKey)
        newTrainingChartSegment.push(item)

    })

    // console.log(newTrainingChartSegment)

    state.trainingChartSegment = newTrainingChartSegment

}

const CreateTrainingChartSegmentObject = (state, weekOfTheYearKey) => {

    var segmentObject = {
        'weekOfTheYearKey': weekOfTheYearKey.toString(),
        'trainingChartDataKey': 'empty',
        'programTableColumn': null,
        'overviewFile': null,
        'workoutTemplateCompletionData': {
            'loading': false,
            'strength-session-a': null,
            'strength-session-b': null,
            'mobility-a': null,
            'mobility-b': null
        }
    }

    // Check past data

    for (let item of state.pastData) {

        if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey)) {

            segmentObject.trainingChartDataKey = 'past'
            segmentObject.overviewFile = item
            segmentObject.workoutTemplateCompletionData.loading = true

            return segmentObject

        }

    }

    // Check active data

    if (state.activeData !== null && new WeekOfTheYearKey(state.activeData.weekOfTheYearKey).equals(weekOfTheYearKey)) {

        segmentObject.trainingChartDataKey = 'active'
        segmentObject.workoutTemplateCompletionData.loading = true
        segmentObject.overviewFile = state.activeData

    }

    // Check future data

    if (state.latestProgramSnapshot !== null && state.latestProgramSnapshot.programTable !== null) {

        for (let item of state.latestProgramSnapshot.programTable) {

            if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey)) {

                // For athlete profile, only show future weeks if they are in the present or future
                if (state.trainingChartUILocation === 'athlete-profile') {

                    if (
                        new WeekOfTheYearKey(state.trainingChartPresentWeekOfTheYearKey).equals(new WeekOfTheYearKey(item.weekOfTheYearKey))
                        || new WeekOfTheYearKey(state.trainingChartPresentWeekOfTheYearKey).lessThan(new WeekOfTheYearKey(item.weekOfTheYearKey))
                    ) {

                        // If the object is already active, splice together active and future data

                        if (segmentObject.trainingChartDataKey === 'active') {

                            segmentObject.programTableColumn = item

                            return segmentObject

                        }

                        // Otherwise just add the future data

                        if (segmentObject.trainingChartDataKey === 'empty') {

                            segmentObject.trainingChartDataKey = 'future'
                            segmentObject.programTableColumn = item

                            return segmentObject

                        }

                    }


                }

                // For snapshot generator, display future weeks even if they are in the past
                if (state.trainingChartUILocation === 'snapshot-generator') {

                    // If the object is already active, splice together active and future data

                    if (segmentObject.trainingChartDataKey === 'active') {

                        segmentObject.programTableColumn = item

                        return segmentObject

                    }

                    // Otherwise just add the future data

                    if (segmentObject.trainingChartDataKey === 'empty') {

                        segmentObject.trainingChartDataKey = 'future'
                        segmentObject.programTableColumn = item

                        return segmentObject

                    }

                }

            }

        }

    }

    return segmentObject

}

const GetTrainingChartWeekData = (state, weekOfTheYearKey) => {

    // First check past data
    for (let item of state.pastData) {

        if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey)) return item

    }

    // Then check active data
    if (state.activeData !== null && new WeekOfTheYearKey(state.activeData.weekOfTheYearKey).equals(weekOfTheYearKey)) {
        return state.activeData
    }

    // Then check future data
    if (state.latestProgramSnapshot !== null && state.latestProgramSnapshot.programTable !== null) {

        for (let item of state.latestProgramSnapshot.programTable) {

            if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey))
                return item

        }

    }

    return null

}

const GetActiveARaceProgramTableColumn = (state, weekOfTheYearKey) => {

    if (state.latestProgramSnapshot !== null && state.latestProgramSnapshot.programTable !== null) {

        for (let item of state.latestProgramSnapshot.programTable) {
            if (new WeekOfTheYearKey(item.weekOfTheYearKey).equals(weekOfTheYearKey))
                return item
        }

    }

    return null

}

export const {
    initializeGroupProfileData,
    initializeAthleteProfileData,
    setTrainingChartSelectedWeekOfTheYearKey,
    setActiveWeekWeekDaySchedule,
    setActiveWeekCompletionData,
    addOverrideWeekDaySchedule,
    removeOverrideWeekDaySchedule,
    addTrainingPeaksWorkoutCalendarEventItems,
    setCoachInviteEmail,
    setLatestCoachInviteTimestamp,
    addNewProgramTableOverride,
    removeProgramTableOverride,
    clearProgramTableOverrides,
    setWorkoutTemplateOverrideWeightRecommendationStorage,
    updateWorkoutTemplateOverrideWeightRecommendationStorage,
    clearWorkoutTemplateOverrideWeightRecommendationStorage,
    setTrainingChartSegmentDomain,
    resetAthleteProfileState,
    TEST_setPresentDate,
    TEST_setPresentWeekOfTheYearKey
} = athleteProfileSlice.actions;

export default athleteProfileSlice.reducer;